/**
 * HabitDetail — Panel for viewing and editing a single habit's details.
 *
 * Displays an edit form with:
 * - Name input (1-100 chars)
 * - Description textarea (0-500 chars)
 * - Target time picker (HH:mm)
 * - Color picker
 * - Current streak and longest streak display
 * - Archive action with confirmation
 *
 * Uses useHabitStore for updates and archiving.
 *
 * @see Requirements 8.1, 8.6, 8.8
 */

import { useState, useCallback, useEffect } from 'react';
import type { Habit } from '../../types';
import { useHabitStore } from '../../stores';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { StreakCounter } from './StreakCounter';
import { HabitMonthCalendar } from './HabitMonthCalendar';

export interface HabitDetailProps {
  /** The habit to display/edit. */
  habit: Habit;
  /** Called when the user closes the detail panel. */
  onClose: () => void;
}

/** Predefined color palette for habit color selection. */
const COLOR_OPTIONS = [
  '#F5A5B8', // rose
  '#B4A7D6', // lavender
  '#B8D4E3', // sky blue
  '#F2D4A7', // warm gold
  '#A8D5BA', // sage green
  '#E6A8D7', // orchid
  '#FFB7B2', // coral
  '#C9B1FF', // violet
];

export function HabitDetail({ habit, onClose }: HabitDetailProps) {
  const updateHabit = useHabitStore((s) => s.updateHabit);
  const archiveHabit = useHabitStore((s) => s.archiveHabit);

  // Form state
  const [name, setName] = useState(habit.name);
  const [description, setDescription] = useState(habit.description);
  const [targetTime, setTargetTime] = useState(habit.targetTime || '');
  const [color, setColor] = useState(habit.color || COLOR_OPTIONS[0]);

  // UI state
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState<string | undefined>(undefined);

  // Reset form when habit changes
  useEffect(() => {
    setName(habit.name);
    setDescription(habit.description);
    setTargetTime(habit.targetTime || '');
    setColor(habit.color || COLOR_OPTIONS[0]);
    setShowArchiveConfirm(false);
  }, [habit]);

  const validateName = useCallback((value: string): string | undefined => {
    if (value.trim().length === 0) return 'Name is required';
    if (value.length > 100) return 'Name must be 100 characters or less';
    return undefined;
  }, []);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setName(value);
      setNameError(validateName(value));
    },
    [validateName]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      if (value.length <= 500) {
        setDescription(value);
      }
    },
    []
  );

  const handleSave = useCallback(async () => {
    const error = validateName(name);
    if (error) {
      setNameError(error);
      return;
    }

    setIsSaving(true);
    try {
      await updateHabit(habit.habitId, {
        name: name.trim(),
        description,
        targetTime: targetTime || null,
        color: color || null,
      });
      onClose();
    } catch {
      // Error is handled by the store
    } finally {
      setIsSaving(false);
    }
  }, [name, description, targetTime, color, habit.habitId, updateHabit, onClose, validateName]);

  const handleArchive = useCallback(async () => {
    try {
      await archiveHabit(habit.habitId);
      onClose();
    } catch {
      // Error is handled by the store
    }
  }, [habit.habitId, archiveHabit, onClose]);

  const handleColorSelect = useCallback((selectedColor: string) => {
    setColor(selectedColor);
  }, []);

  const hasChanges =
    name !== habit.name ||
    description !== habit.description ||
    (targetTime || null) !== (habit.targetTime || null) ||
    (color || null) !== (habit.color || null);

  return (
    <Card
      className="w-full max-w-md"
      header={
        <div className="flex items-center justify-between">
          <h2 className="text-heading-3 font-semibold text-text-heading">
            Edit Habit
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-sm text-text-secondary hover:bg-hover-bg transition-colors duration-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
            aria-label="Close habit detail"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        className="flex flex-col gap-4"
      >
        {/* Name input */}
        <Input
          label="Name"
          value={name}
          onChange={handleNameChange}
          error={nameError}
          maxLength={100}
          placeholder="Habit name"
          required
        />

        {/* Description textarea */}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="habit-description"
            className="text-sm font-medium text-text-secondary"
          >
            Description
          </label>
          <textarea
            id="habit-description"
            value={description}
            onChange={handleDescriptionChange}
            maxLength={500}
            placeholder="Optional description"
            rows={3}
            className={[
              'w-full rounded-sm border bg-bg-card text-text-primary placeholder:text-text-secondary/60',
              'transition-all duration-fast ease-fast',
              'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-secondary',
              'border-border hover:border-secondary-light',
              'resize-none px-4 py-2 text-body',
            ].join(' ')}
          />
          <span className="text-xs text-text-secondary text-right">
            {description.length}/500
          </span>
        </div>

        {/* Target time picker */}
        <Input
          label="Target Time"
          type="time"
          value={targetTime}
          onChange={(e) => setTargetTime(e.target.value)}
          placeholder="HH:mm"
        />

        {/* Color picker */}
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text-secondary">
            Color
          </span>
          <div
            className="flex flex-wrap gap-2"
            role="radiogroup"
            aria-label="Habit color"
          >
            {COLOR_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={color === option}
                aria-label={`Color ${option}`}
                onClick={() => handleColorSelect(option)}
                className={[
                  'w-8 h-8 rounded-full border-2 transition-all duration-default',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2',
                  color === option
                    ? 'border-text-heading scale-110 shadow-card'
                    : 'border-transparent hover:scale-105',
                ].join(' ')}
                style={{ backgroundColor: option }}
              />
            ))}
          </div>
        </div>

        {/* Streak display */}
        <div className="flex items-center gap-4 py-2 px-3 bg-bg-secondary rounded-sm">
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs text-text-secondary">Current</span>
            <StreakCounter streak={habit.currentStreak} color={color || undefined} />
          </div>
          <div className="w-px h-8 bg-border" aria-hidden="true" />
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs text-text-secondary">Longest</span>
            <span className="text-sm font-semibold text-text-heading">
              {habit.longestStreak} days
            </span>
          </div>
        </div>

        {/* Interactive month calendar — click any day to toggle completion */}
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text-secondary">
            Calendar
          </span>
          <HabitMonthCalendar habit={habit} />
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between pt-2">
          {/* Archive button */}
          {!showArchiveConfirm ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowArchiveConfirm(true)}
              aria-label="Archive this habit"
            >
              Archive
            </Button>
          ) : (
            <div className="flex items-center gap-2" role="alert">
              <span className="text-sm text-text-secondary">Archive?</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleArchive}
                aria-label="Confirm archive habit"
              >
                Yes
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowArchiveConfirm(false)}
                aria-label="Cancel archive"
              >
                No
              </Button>
            </div>
          )}

          {/* Save button */}
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={!hasChanges || !!nameError || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

HabitDetail.displayName = 'HabitDetail';
